import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Card, CardContent, Typography, Button, Box } from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import RefreshIcon from '@material-ui/icons/Refresh';

const useStyles = makeStyles((theme) => ({
  errorCard: {
    margin: theme.spacing(2),
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '8px',
    maxWidth: '500px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  errorContent: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  errorIcon: {
    fontSize: 32,
    marginBottom: theme.spacing(2),
    color: '#856404',
  },
  errorTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#856404',
    marginBottom: theme.spacing(1),
  },
  errorMessage: {
    fontSize: '14px',
    color: '#856404',
    marginBottom: theme.spacing(2),
  },
  retryButton: {
    backgroundColor: '#856404',
    color: 'white',
    '&:hover': {
      backgroundColor: '#6c5203',
    },
  },
}));

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to monitoring service
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Send error to monitoring service (replace with your preferred service)
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService = (error, errorInfo) => {
    // Replace with your preferred error monitoring service
    // Examples: Sentry, LogRocket, Bugsnag, etc.
    try {
      // Example: Send to a custom error endpoint
      fetch('/api/errors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          error: error.toString(),
          errorInfo: errorInfo.componentStack,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href,
        }),
      }).catch(() => {
        // Silently fail if error reporting fails
      });
    } catch (e) {
      // Silently fail if error reporting fails
    }
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return <ErrorFallback onRetry={this.handleRetry} error={this.state.error} />;
    }

    return this.props.children;
  }
}

const ErrorFallback = ({ onRetry, error }) => {
  const classes = useStyles();

  return (
    <Card className={classes.errorCard}>
      <CardContent className={classes.errorContent}>
        <ErrorOutlineIcon className={classes.errorIcon} />
        <Typography className={classes.errorTitle}>
          Something went wrong
        </Typography>
        <Typography className={classes.errorMessage}>
          We're sorry, but something unexpected happened. Please try refreshing the page.
        </Typography>
        {process.env.NODE_ENV === 'development' && error && (
          <Box mt={2}>
            <Typography variant="body2" style={{ color: '#856404', fontSize: '12px' }}>
              Error: {error.toString()}
            </Typography>
          </Box>
        )}
        <Button
          variant="contained"
          className={classes.retryButton}
          startIcon={<RefreshIcon />}
          onClick={onRetry}
        >
          Try Again
        </Button>
      </CardContent>
    </Card>
  );
};

export default ErrorBoundary;
